import { ImagesFormat, LocationProps } from "@/types";

export const parseBoolean = (val: any): boolean => {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val === 1;
  if (typeof val === 'string') {
    const clean = val.trim().toLowerCase();
    return clean === 'sí' || clean === 'si' || clean === 'yes' || clean === 'true' || clean === '1';
  }
  return false;
};

export const parseNumber = (val: any): number | null => {
  if (val === null || val === undefined || val === '') return null;
  const parsed = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.-]/g, ''));
  return isNaN(parsed) ? null : parsed;
};

export const parseDate = (val: any): Date | null => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === 'number') {
    // Excel serial date formula to JS Date
    const date = new Date(Math.round((val - 25569) * 86400 * 1000));
    return isNaN(date.getTime()) ? null : date;
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    // Match DD/MM/YYYY or DD-MM-YYYY
    const dmy = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (dmy) {
      const day = parseInt(dmy[1], 10);
      const month = parseInt(dmy[2], 10) - 1;
      const year = parseInt(dmy[3], 10);
      const date = new Date(year, month, day);
      return isNaN(date.getTime()) ? null : date;
    }
    const parsed = new Date(trimmed);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

export const parseString = (val: any): string => {
  if (val === null || val === undefined) return "";
  return String(val).trim();
};

export const computeAcademicLevel = (levelStudy: string, providedAcademicLevel?: string): string => {
  const clean = (levelStudy || "").toLowerCase().trim();
  if (
    clean.includes("posgrado") ||
    clean.includes("postgrado") ||
    clean.includes("especializaci") ||
    clean.includes("maestr") ||
    clean.includes("doctorad")
  ) {
    return "Posgrado";
  }
  if (clean.includes("pregrado") || clean.includes("técnico") || clean.includes("tecnico") || clean.includes("tecnólogo") || clean.includes("tecnologo")) {
    return "Pregrado";
  }
  if (providedAcademicLevel && providedAcademicLevel.trim()) return providedAcademicLevel.trim();
  return "Pregrado";
};

export function convertProgramObject(
  programObject: any,
  instituteName: string,
  headquarter: LocationProps[],
  facultyId: string,
  instituteId: string,
  iesCharacter: string,
  iesImages: ImagesFormat[],
  iesLogo: string,
  subscriptionLevel: number,
  facultyName: string
) {
  const code = parseString(programObject.CÓDIGO_SNIES_PROGRAMA);
  const levelStudy = parseString(programObject.NIVEL_FORMACIÓN);
  const levelStudyDetail = parseString(programObject.NIVEL_FORMACIÓN_DETALLADO_SEGÚN_MINISTERIO) || levelStudy;
  const academicLevel = computeAcademicLevel(levelStudy, programObject.NIVEL_ACADÉMICO);
  const profileGraduated = parseString(programObject.PERFIL_DEL_GRADUADO);

  const convertedObject = {
    active: true,
    code: code,
    id: code,
    name: parseString(programObject.NOMBRE_DEL_PROGRAMA),
    levelStudy: levelStudy,
    academicLevel: academicLevel,
    levelStudyDetail: levelStudyDetail,
    price: parseNumber(programObject.PRECIO_PROGRAMA_POR_PERIODO),
    currency: parseString(programObject.TIPO_DE_MONEDA) || "COP",
    modalities: parseString(programObject.MODALIDAD),
    schedule: parseString(programObject.TIPO_HORARIO),
    credits: parseNumber(programObject.NÚMERO_CRÉDITOS_PROGRAMA),
    duration: parseNumber(programObject.NÚMERO_PERIODOS_DURACIÓN),
    periods: parseString(programObject.TIPO_PERIODO),
    description: parseString(programObject.DESCRIPCIÓN_PROGRAMA),
    obtainedTitle: parseString(programObject.TÍTULO_OTORGADO || programObject.TITULO_OTORGADO),
    profileGraduated: profileGraduated,

    webSite: parseString(programObject.SITIO_WEB_PROGRAMA),
    costCredit: parseNumber(programObject.COSTO_DEL_CRÉDITO),
    startYear: parseDate(programObject.FECHA_INICIO_PROGRAMA),
    inscriptions: parseDate(programObject.PRÓXIMAS_INSCRIPCIONES),
    startClass: parseDate(programObject.INICIO_DE_CLASES),
    contactEmail: parseString(programObject.EMAIL_DEL_PROGRAMA),
    contactPhone: parseNumber(programObject.TELÉFONO_CONTACTO_PROGRAMA),
    phone_code_country: "+57",
    nationalAccreditation: parseBoolean(programObject.ACREDITACIÓN_NACIONAL),
    internationalAccreditation: parseBoolean(programObject.ACREDITACIÓN_INTERNACIONAL),
    exchange: parseBoolean(programObject.INTERCAMBIOS),
    dualProgram: parseBoolean(programObject.DOBLE_TITULACIÓN),
    summerProgram: parseBoolean(programObject.PROGRAMAS_DE_VERANO),
    mandatoryInterships: parseBoolean(programObject.PRÁCTICAS_OBLIGATORIAS),
    mandatoryThesis: parseBoolean(programObject.TESIS_O_TRABAJO_DE_GRADO_OBLIGATORIO),
    candidateProfile: parseString(programObject.PERFIL_DEL_CANDIDATO) || profileGraduated,
    admissionsPeriodicity: parseString(programObject.PERIODICIDAD_DE_LAS_ADMISIONES),

    institution: instituteName,
    headquarter: headquarter,
    uidFaculty: facultyId,
    uidInstitution: instituteId,
    iesCharacter: iesCharacter,
    iesImages: iesImages || [],
    iesLogo: iesLogo || "",
    facultyName: facultyName,
    subscriptionLevel: subscriptionLevel,
    ref: `institutes/${instituteId}/faculties/${facultyId}/careers/${code}`,
    compatiblePrograms: [],
  };

  return convertedObject;
}